import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation } from 'convex/react';
import type { FunctionReturnType } from 'convex/server';
import { api } from '../../../convex/_generated/api';
import { authClient } from '../../lib/auth-client';
import { rememberSignInMethod } from '../../lib/signInSetup';
import CodeInput from './CodeInput';
import { Button, Field, Notice, Picture, StepBars, headingClasses, type IconName } from './AuthUi';

export type SignInSettings = FunctionReturnType<typeof api.passwordlessAuth.getSettings>;

type Step = 'offer' | 'code' | 'expired' | 'inUse' | 'done';
type Problem = 'invalid' | 'expired' | 'wait' | 'too_many' | 'stale_session' | 'invalid_email' | 'email_in_use' | 'not_available' | 'network';

const RESEND_SECONDS = 60; // matches the server's minimum gap between codes
const REQUEST_TIMEOUT_MS = 20000;

const MESSAGES: Partial<Record<Problem, string>> = {
  invalid: "That code doesn't match. Check the email and try again.",
  wait: 'Please wait a minute, then try again.',
  // Better Auth reports the 60-second gap and the hourly cap alike, so this
  // must be true of both.
  too_many: "We can't send another code just yet. Please try again later. You can do this any time from Sign-in options on your dashboard.",
  stale_session: 'For your safety, please sign in again to finish.',
  invalid_email: 'Please check your email address. It should look like name@example.co.uk.',
  not_available: "Email codes aren't available for your account. You can keep using your password, or contact us for help.",
  network: 'Something went wrong. Please try again.',
};

function withTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('timeout')), REQUEST_TIMEOUT_MS);
    promise.then(resolve, reject).finally(() => window.clearTimeout(timer));
  });
}

/** Map Better Auth email-code errors to the guide's plain-language problems. */
function problemFromAuthError(error: { code?: string; status?: number }, sending: boolean): Problem {
  if (error.status === 429) return 'too_many';
  if (error.status === 401) return 'stale_session';
  if (error.code === 'OTP_EXPIRED' || error.code === 'TOO_MANY_ATTEMPTS') return 'expired';
  return sending ? 'not_available' : 'invalid';
}

const countdown = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

interface EmailCodeSetupProps {
  settings: SignInSettings;
  /** 'guide' is the full-page walkthrough; 'inline' sits in the Sign-in options tab. */
  variant: 'guide' | 'inline';
  /** Guide only: the customer chose not to set this up now. */
  onNotNow?: () => void;
  /** Guide only: leave the finished guide. */
  onDone?: () => void;
  onSignInAgain: () => void;
}

/**
 * Turn on email-code sign-in in plain steps: confirm the existing sign-in email,
 * or (for imported placeholder addresses) add a real one, by entering a code.
 */
export default function EmailCodeSetup({ settings, variant, onNotNow, onDone, onSignInAgain }: EmailCodeSetupProps) {
  // Fix the route when the flow starts: settings update live once the email is
  // confirmed, and must not switch the flow part-way through.
  const [addingEmail] = useState(settings.needsSignInEmail);
  const [email, setEmail] = useState(settings.needsSignInEmail ? settings.suggestedEmail ?? '' : settings.email ?? '');
  const [suggested] = useState(Boolean(settings.needsSignInEmail && settings.suggestedEmail));
  const [sentTo, setSentTo] = useState('');
  const [step, setStep] = useState<Step>('offer');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const startSignInEmail = useMutation(api.signInEmailSetup.startSignInEmail);
  const confirmSignInEmail = useMutation(api.signInEmailSetup.confirmSignInEmail);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const firstScreen = useRef(true);
  // A ref as well as state: typing the sixth digit and pressing Confirm can
  // both arrive before the busy state is applied.
  const verifying = useRef(false);
  const guide = variant === 'guide';
  const idPrefix = `email-code-${variant}`;

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn(seconds => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  // Announce each new screen: focus the code box, otherwise the heading. The
  // settings tab does not steal focus when it first appears.
  useEffect(() => {
    const initial = firstScreen.current;
    firstScreen.current = false;
    if (initial && !guide) return;
    if (step === 'code') codeRef.current?.focus();
    else headingRef.current?.focus();
  }, [step, guide]);

  const sendRequest = useRef(0);
  // The most recent send known to have worked; older ones that finish late
  // must not replace its code screen.
  const shownRequest = useRef(0);

  const showCodeStep = (request: number, target: string) => {
    if (request < shownRequest.current) return;
    shownRequest.current = request;
    setProblem(null);
    setSentTo(target);
    setCode('');
    setResendIn(RESEND_SECONDS);
    setStep('code');
  };

  const send = async () => {
    const target = email.trim().toLowerCase();
    if (!target) { setProblem('invalid_email'); return; }
    setBusy(true);
    setProblem(null);
    const request = ++sendRequest.current;
    const attempt: Promise<Problem | null> = addingEmail
      ? startSignInEmail({ email: target }).then(({ status }) => status === 'sent' ? null : status)
      : authClient.emailOtp.sendVerificationOtp({ email: target, type: 'email-verification' })
        .then(response => response.error ? problemFromAuthError(response.error, true) : null);
    try {
      const result = await withTimeout(attempt);
      if (result) {
        setProblem(result);
        if (result === 'wait') setResendIn(RESEND_SECONDS);
        return;
      }
      showCodeStep(request, target);
    } catch {
      setProblem('network');
      // If the code is sent after all, let the customer type it in, even if a
      // retry since then was refused, unless a newer code has been sent.
      attempt
        .then(late => { if (late === null) showCodeStep(request, target); })
        .catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value: string) => {
    if (verifying.current || !/^\d{6}$/.test(value)) return;
    verifying.current = true;
    setBusy(true);
    setProblem(null);
    let retry = false;
    try {
      let result: Problem | null;
      if (addingEmail) {
        const { status } = await withTimeout(confirmSignInEmail({ code: value }));
        result = status === 'done' ? null : status === 'invalid_code' ? 'invalid' : status;
      } else {
        const response = await withTimeout(authClient.emailOtp.verifyEmail({ email: sentTo, otp: value }));
        result = response.error ? problemFromAuthError(response.error, false) : null;
      }
      if (!result) {
        rememberSignInMethod('email');
        setStep('done');
      } else if (result === 'expired') {
        setStep('expired');
      } else if (result === 'email_in_use') {
        setStep('inUse');
      } else {
        setCode('');
        setProblem(result);
        retry = result === 'invalid';
      }
    } catch {
      setProblem('network');
    } finally {
      verifying.current = false;
      setBusy(false);
    }
    if (retry) window.requestAnimationFrame(() => codeRef.current?.focus());
  };

  // A confirmation can still succeed after the screen gave up waiting for it
  // (or on another device). Settings update live, so show success when they
  // say the address we sent the code to is now confirmed.
  useEffect(() => {
    if (step !== 'code' || !sentTo || !settings.emailVerified || settings.email !== sentTo) return;
    rememberSignInMethod('email');
    setProblem(null);
    setStep('done');
  }, [step, sentTo, settings.emailVerified, settings.email]);

  const backToOffer = (clearEmail: boolean) => {
    if (clearEmail) setEmail('');
    setCode('');
    setProblem(null);
    setStep('offer');
  };

  // The guide is a page of its own; in Sign-in options this sits under "Email code".
  const heading = (text: string) => guide
    ? <h1 ref={headingRef} tabIndex={-1} className={`${headingClasses.guide} focus:outline-none`}>{text}</h1>
    : <h4 ref={headingRef} tabIndex={-1} className="text-[17px] font-semibold text-asg-ink focus:outline-none">{text}</h4>;
  const body = (text: ReactNode) => <p className="text-[17px] leading-6 text-asg-ink">{text}</p>;
  const picture = (name: IconName, current?: number) => guide && <div className="space-y-6">
    {current && <StepBars current={current} total={3} />}
    <Picture name={name} />
  </div>;

  const problemId = `${idPrefix}-problem`;
  const alert = problem && MESSAGES[problem] ? <Notice tone="danger" id={problemId}>{MESSAGES[problem]}</Notice> : null;
  const stale = problem === 'stale_session';
  const signInAgain = <Button onClick={onSignInAgain}>Sign in again</Button>;

  // "Skip for now" in the guide; "Cancel" back to the start in Sign-in options.
  const leave = guide
    ? onNotNow && <Button variant="quiet" disabled={busy} onClick={onNotNow}>Skip for now</Button>
    : <Button variant="quiet" disabled={busy} onClick={() => backToOffer(false)}>Cancel</Button>;

  const resendLabel = resendIn > 0 ? `Send a new code (${countdown(resendIn)})` : 'Send a new code';
  const block = `space-y-4 ${guide ? 'text-center' : ''}`;
  const passwordStillWorks = <p className="flex items-center justify-center gap-1.5 text-[13px] text-asg-slate">
    Your password will still work.
  </p>;

  if (step === 'offer') {
    return <div className={block}>
      {picture('mail', 1)}
      {heading(guide ? 'Sign in the easy way next time' : 'Turn on email codes')}
      {body(addingEmail
        ? "Add your email and we'll send you a 6‑digit code to sign in, instead of typing your password."
        : 'Instead of typing your password, we can email you a 6‑digit code.')}
      <form noValidate onSubmit={event => { event.preventDefault(); if (!stale) void send(); }} className="space-y-4">
        {addingEmail
          ? <Field id={`${idPrefix}-email`} label="Your email" type="email" inputMode="email" autoComplete="email" maxLength={254}
            value={email} disabled={busy} invalid={problem === 'invalid_email'} describedBy={alert ? problemId : undefined}
            hint={suggested ? "We've filled this in from your contact details. You can change it." : undefined}
            onChange={event => { setEmail(event.target.value); if (problem === 'invalid_email') setProblem(null); }} />
          : <div>
            <p className="text-[13px] text-asg-slate">We'll send it to</p>
            <p className="text-[17px] font-semibold text-asg-ink [overflow-wrap:anywhere]">{email}</p>
          </div>}
        {alert}
        {stale ? signInAgain : <Button type="submit" loading={busy} disabled={resendIn > 0}>
          {addingEmail ? 'Send me a code' : 'Turn on email codes'}
          {resendIn > 0 && ` (${countdown(resendIn)})`}
        </Button>}
      </form>
      {guide && onNotNow && <Button variant="quiet" disabled={busy} onClick={onNotNow}>Not now</Button>}
      {passwordStillWorks}
    </div>;
  }

  if (step === 'code') {
    return <div className={block}>
      {picture('inbox', 2)}
      {heading('Check your email')}
      {body(<>We've sent a 6‑digit code to <strong className="font-semibold [overflow-wrap:anywhere]">{sentTo}</strong>. Type it below.</>)}
      {alert}
      <form noValidate onSubmit={event => { event.preventDefault(); void verify(code); }} className="space-y-4">
        <CodeInput ref={codeRef} id={`${idPrefix}-code`} label="6-digit code" value={code} disabled={busy}
          invalid={problem === 'invalid'} describedBy={alert ? problemId : undefined}
          hint="Can't see it? Check your junk or spam folder. The code works for 5 minutes."
          onChange={value => { setCode(value); if (problem === 'invalid') setProblem(null); }}
          onComplete={value => void verify(value)} />
        {stale ? signInAgain : <Button type="submit" loading={busy} disabled={code.length !== 6}>Confirm</Button>}
      </form>
      <div>
        <Button variant="quiet" disabled={busy || resendIn > 0} onClick={() => void send()}>{resendLabel}</Button>
        {addingEmail && <Button variant="quiet" disabled={busy} onClick={() => backToOffer(false)}>Use a different email</Button>}
        {leave}
      </div>
    </div>;
  }

  if (step === 'expired') {
    return <div className={block}>
      {picture('clock')}
      {heading('That code has stopped working')}
      {body("Codes only last 5 minutes, or stop after 3 wrong tries. We'll send you a new one.")}
      {alert}
      {stale ? signInAgain : <Button loading={busy} disabled={resendIn > 0} onClick={() => void send()}>{resendLabel}</Button>}
      {leave}
    </div>;
  }

  if (step === 'inUse') {
    return <div className={block}>
      {picture('people')}
      {heading('This email is already used')}
      {body(<>Another account already signs in with <strong className="font-semibold [overflow-wrap:anywhere]">{sentTo}</strong>. Each email can only be used for one account.</>)}
      {body('Got more than one property with us? Use a different email for this one, or skip for now.')}
      <Button onClick={() => backToOffer(true)}>Use a different email</Button>
      {leave}
    </div>;
  }

  return <div className={block}>
    {picture('done', 3)}
    {heading('Email codes are turned on')}
    {body("Next time, enter your email when you sign in and we'll offer to email you a 6‑digit code.")}
    {passwordStillWorks}
    {onDone && <Button onClick={onDone}>Go to my readings</Button>}
  </div>;
}
